import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Container,
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
    Popover,
    Chip,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Card,
    CardActionArea,
    CardContent,
    useTheme,
    Skeleton,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { Global } from '@emotion/react';
import PersonIcon from '@mui/icons-material/Person';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { dataPrincipalsService, DataPrincipalFilters, DataPrincipalListItem, DataPrincipalStats, DPRightsRequestsSummary } from '../../services/dataPrincipals-service';
import { useNavigate } from 'react-router-dom';
import { formatFriendlyDate } from '../../utils/dateUtils';
import AddDataPrincipalPanel from './AddDataPrincipalPanel';
import SendConsentNoticeModal from './components/SendConsentNoticeModal';
import BulkAssignNoticeModal from './components/BulkAssignNoticeModal';

type SearchType = "DPID" | "TOKEN" | "EXTERNAL_ID" | "EMAIL" | "PHONE";

type StatCardPreset = 'total' | 'activeConsent' | 'withdrawnConsent' | 'test';

// Country codes for phone numbers (India first as default)
const COUNTRY_CODES: { code: string; dialCode: string; name: string }[] = [
  { code: 'IN', dialCode: '+91', name: 'India' },
  { code: 'US', dialCode: '+1', name: 'United States' },
  { code: 'GB', dialCode: '+44', name: 'United Kingdom' },
  { code: 'AU', dialCode: '+61', name: 'Australia' },
  { code: 'CA', dialCode: '+1', name: 'Canada' },
  { code: 'DE', dialCode: '+49', name: 'Germany' },
  { code: 'FR', dialCode: '+33', name: 'France' },
  { code: 'JP', dialCode: '+81', name: 'Japan' },
  { code: 'CN', dialCode: '+86', name: 'China' },
  { code: 'SG', dialCode: '+65', name: 'Singapore' },
];

// Get consent status description
const getConsentStatusDescription = (status: string): string => {
    switch (status.toUpperCase()) {
        case 'ACTIVE':
            return 'Has at least one active CONSENT-based purpose with consent given, not expired, and not withdrawn.';
        case 'WITHDRAWN':
            return 'All CONSENT-based purposes that had consent given have been withdrawn by the data principal.';
        case 'EXPIRED':
            return 'All CONSENT-based purposes with consent given have expired based on their retention expiration dates.';
        case 'INACTIVE':
            return 'Has CONSENT-based purposes but none are currently active (consent denied, expired, or withdrawn).';
        case 'UNKNOWN':
            return 'No CONSENT-based consent records found for this data principal.';
        default:
            return '';
    }
};

// Consent status color mapping - theme-aware
const getConsentStatusColor = (status: string, isDark: boolean) => {
    if (isDark) {
        switch (status.toUpperCase()) {
            case 'ACTIVE':
                return { bg: '#1B5E20', color: '#81C784', border: '#28A745' };
            case 'WITHDRAWN':
                return { bg: '#4A1E1E', color: '#E57373', border: '#DC3545' };
            case 'EXPIRED':
                return { bg: '#3E3B00', color: '#FFD54F', border: '#FFC107' };
            case 'INACTIVE':
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
            case 'UNKNOWN':
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
            default:
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
        }
    } else {
        switch (status.toUpperCase()) {
            case 'ACTIVE':
                return { bg: '#D4EDDA', color: '#155724', border: '#28A745' };
            case 'WITHDRAWN':
                return { bg: '#F8D7DA', color: '#721C24', border: '#DC3545' };
            case 'EXPIRED':
                return { bg: '#FFF3CD', color: '#856404', border: '#FFC107' };
            case 'INACTIVE':
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
            case 'UNKNOWN':
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
            default:
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
        }
    }
};

// Rights request type icons
const getRequestTypeIcon = (type: string) => {
    switch (type) {
        case 'DATA_ACCESS':
            return '🔍';
        case 'DATA_CORRECTION':
            return '📝';
        case 'DATA_ERASURE':
            return '🗑️';
        case 'DATA_PORTABILITY':
            return '📦';
        case 'CONSENT_WITHDRAWAL':
            return '❌';
        default:
            return '📋';
    }
};

const DataPrincipals: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    // State management
    const [dataPrincipals, setDataPrincipals] = useState<DataPrincipalListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Summary state
    const [stats, setStats] = useState<DataPrincipalStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    // Filter state
    const [filters, setFilters] = useState<DataPrincipalFilters>({
        page: 1,
        pageSize: 20
    });

    // Search UI state (submit-on-enter/click to avoid refreshing on each keystroke)
    const [searchType, setSearchType] = useState<SearchType>("DPID");
    const [searchInput, setSearchInput] = useState<string>('');
    const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [searchError, setSearchError] = useState<string | null>(null);

    // Filter UI state
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [tempFilters, setTempFilters] = useState<DataPrincipalFilters>({
        page: 1,
        pageSize: 20
    });
    const [filterLoading, setFilterLoading] = useState(false);

    // Bulk selection state
    const [selectedDPs, setSelectedDPs] = useState<Set<number>>(new Set());

    // Rights requests summary cache (per DP)
    const [rightsRequestsCache, setRightsRequestsCache] = useState<Map<number, DPRightsRequestsSummary>>(new Map());

    // Add panel state
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [isSendConsentModalOpen, setIsSendConsentModalOpen] = useState(false);
    const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);

    // Data fetching effects
    useEffect(() => {
        loadDataPrincipals();
    }, [page, pageSize, filters]);

    useEffect(() => {
        loadStats();
    }, []);

    // Keep the search box in sync with applied filters (e.g., when cleared programmatically)
    useEffect(() => {
        if (filters.search) {
            setSearchInput(filters.search);
        } else {
            setSearchInput('');
            setPhoneNumber('');
        }
    }, [filters.search]);

    // Validation function for search input
    const validateSearchInput = (): { valid: boolean; error: string | null } => {
        if (searchType === "PHONE") {
            const cleanedPhone = phoneNumber.trim().replace(/\s+/g, "");
            if (!cleanedPhone) {
                return { valid: false, error: "Please enter a phone number" };
            }
            if (!/^\d+$/.test(cleanedPhone)) {
                return { valid: false, error: "Phone number must contain only digits" };
            }
            if (phoneCountryCode === "+91") {
                if (cleanedPhone.length !== 10) {
                    return { valid: false, error: "Indian phone number must be 10 digits" };
                }
                if (!/^[6-9]/.test(cleanedPhone)) {
                    return { valid: false, error: "Indian phone number must start with 6, 7, 8, or 9" };
                }
            } else if (phoneCountryCode === "+1") {
                if (cleanedPhone.length !== 10) {
                    return { valid: false, error: "Phone number must be 10 digits" };
                }
            } else {
                if (cleanedPhone.length < 7 || cleanedPhone.length > 15) {
                    return { valid: false, error: "Phone number must be between 7 and 15 digits" };
                }
            }
            return { valid: true, error: null };
        } else if (searchType === "EMAIL") {
            const email = searchInput.trim();
            if (!email) {
                return { valid: false, error: "Please enter an email address" };
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                return { valid: false, error: "Please enter a valid email address" };
            }
            return { valid: true, error: null };
        } else if (searchType === "DPID") {
            const dpId = searchInput.trim();
            if (!dpId) {
                return { valid: false, error: "Please enter a DPId" };
            }
            if (!/^\d+$/.test(dpId)) {
                return { valid: false, error: "DPId must contain only numbers" };
            }
            return { valid: true, error: null };
        } else {
            const value = searchInput.trim();
            if (!value) {
                return { valid: false, error: `Please enter a ${searchType.toLowerCase().replace("_", " ")}` };
            }
            return { valid: true, error: null };
        }
    };

    const handleSearch = () => {
        const validation = validateSearchInput();
        if (!validation.valid) {
            setSearchError(validation.error);
            return;
        }
        setSearchError(null);

        let searchTerm = "";
        if (searchType === "PHONE") {
            const cleanedPhone = phoneNumber.trim().replace(/\s+/g, "");
            let finalPhone = cleanedPhone;
            if (finalPhone.startsWith(phoneCountryCode)) {
                finalPhone = finalPhone.substring(phoneCountryCode.length);
            } else if (finalPhone.startsWith(phoneCountryCode.replace("+", ""))) {
                finalPhone = finalPhone.substring(phoneCountryCode.replace("+", "").length);
            }
            searchTerm = `${phoneCountryCode}${finalPhone}`;
        } else {
            searchTerm = searchInput.trim();
        }

        const newFilters = { 
            ...filters, 
            search: searchTerm.length ? searchTerm : undefined, 
            searchType: searchType, // Pass searchType to backend for optimization
            page: 1 
        };
        setFilters(newFilters);
        setPage(0);
    };

    // Load rights requests summary for visible DPs
    useEffect(() => {
        if (dataPrincipals.length > 0) {
            loadRightsRequestsSummaries();
        }
    }, [dataPrincipals]);

    const loadStats = async () => {
        try {
            setStatsLoading(true);
            const statsData = await dataPrincipalsService.getDataPrincipalStats();
            setStats(statsData);
        } catch (error) {
            console.error('Error loading stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    const loadDataPrincipals = async () => {
        try {
            setLoading(true);
            setError(null);

            const requestFilters: DataPrincipalFilters = {
                ...filters,
                page: page + 1, // Convert 0-based to 1-based for API
                pageSize: pageSize
            };

            const response = await dataPrincipalsService.listDataPrincipals(requestFilters);

            setDataPrincipals(response.dataPrincipals);
            setTotalCount(response.totalCount);
            setTotalPages(response.totalPages);
            setHasNextPage(response.hasNextPage);
            setHasPrevPage(response.hasPrevPage);
        } catch (error) {
            console.error('Error loading data principals:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch data principals');
        } finally {
            setLoading(false);
        }
    };

    const loadRightsRequestsSummaries = async () => {
        // Load summaries for DPs that don't have them cached
        const dpIdsToLoad = dataPrincipals
            .map(dp => dp.dpId)
            .filter(dpId => !rightsRequestsCache.has(dpId));

        if (dpIdsToLoad.length === 0) return;

        // Load in parallel (limit to 10 at a time to avoid overwhelming)
        const batchSize = 10;
        for (let i = 0; i < dpIdsToLoad.length; i += batchSize) {
            const batch = dpIdsToLoad.slice(i, i + batchSize);
            const promises = batch.map(async (dpId) => {
                try {
                    const summary = await dataPrincipalsService.getDPRightsRequestsSummary(dpId);
                    return { dpId, summary };
                } catch (error) {
                    console.error(`Error loading rights requests for DP ${dpId}:`, error);
                    return null;
                }
            });

            const results = await Promise.all(promises);
            setRightsRequestsCache(prev => {
                const newCache = new Map(prev);
                results.forEach(result => {
                    if (result) {
                        newCache.set(result.dpId, result.summary);
                    }
                });
                return newCache;
            });
        }
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPageSize(newPageSize);
        setPage(0);
        setFilters(prev => ({ ...prev, page: 1, pageSize: newPageSize }));
    };

    // Filter UI handlers
    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(event.currentTarget);
        setTempFilters({ ...filters });
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const handleTempFilterChange = (field: keyof DataPrincipalFilters, value: any) => {
        setTempFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = async () => {
        try {
            setFilterLoading(true);
            setFilters({ ...tempFilters, page: 1 });
            setPage(0);
            handleFilterClose();
        } catch (error) {
            console.error('Error applying filters:', error);
        } finally {
            setFilterLoading(false);
        }
    };

    const handleClearFilters = () => {
        const clearedFilters: DataPrincipalFilters = {
            page: 1,
            pageSize: 20
        };
        setFilters(clearedFilters);
        setTempFilters(clearedFilters);
        setPage(0);
    };

    const removeFilter = (key: keyof DataPrincipalFilters) => {
        const newFilters = { ...filters, [key]: undefined, page: 1 };
        setFilters(newFilters);
        setTempFilters(prev => ({ ...prev, [key]: undefined }));
        setPage(0);
    };

    const hasActiveFilters = () => {
        return !!(
            filters.identifierType ||
            filters.consentStatus ||
            filters.isTest !== undefined ||
            filters.isActive !== undefined ||
            filters.startDate ||
            filters.endDate ||
            filters.search
        );
    };

    const handleStatCardClick = (preset: StatCardPreset) => {
        setSearchError(null);
        setPage(0);
        const base: DataPrincipalFilters = { page: 1, pageSize };

        if (preset === 'total') {
            setFilters(base);
            setTempFilters(base);
            return;
        }

        if (preset === 'activeConsent') {
            const next = { ...base, consentStatus: 'ACTIVE' as const };
            setFilters(next);
            setTempFilters(next);
            return;
        }
        if (preset === 'withdrawnConsent') {
            const next = { ...base, consentStatus: 'WITHDRAWN' as const };
            setFilters(next);
            setTempFilters(next);
            return;
        }
        const next = { ...base, isTest: true };
        setFilters(next);
        setTempFilters(next);
    };

    const statCardSelected = (preset: StatCardPreset): boolean => {
        if (preset === 'total') {
            return !hasActiveFilters();
        }
        if (preset === 'activeConsent') {
            return filters.consentStatus === 'ACTIVE';
        }
        if (preset === 'withdrawnConsent') {
            return filters.consentStatus === 'WITHDRAWN';
        }
        return filters.isTest === true;
    };

    const statCardSx = (preset: StatCardPreset) => ({
        minWidth: 160,
        flexShrink: 0,
        bgcolor: 'background.paper',
        border: 2,
        borderColor: statCardSelected(preset) ? 'primary.main' : 'transparent',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:hover': {
            boxShadow: 2,
            borderColor: statCardSelected(preset) ? 'primary.main' : 'divider'
        }
    });

    // Bulk selection handlers
    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedDPs(new Set(dataPrincipals.map(dp => dp.dpId)));
        } else {
            setSelectedDPs(new Set());
        }
    };

    const handleSelectDP = (dpId: number) => {
        setSelectedDPs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(dpId)) {
                newSet.delete(dpId);
            } else {
                newSet.add(dpId);
            }
            return newSet;
        });
    };

    const handleDPClick = (dpId: number) => {
        navigate(`/workbench/data-principals/${dpId}`);
    };

    // Format identifier for display
    const formatIdentifier = (dp: DataPrincipalListItem): string => {
        if (dp.isTest && dp.identifierValue) {
            return dp.identifierValue;
        }
        if (dp.formatPreservingToken) {
            return dp.formatPreservingToken;
        }
        if (dp.vaultId) {
            return `vault:${dp.vaultId}`;
        }
        return 'N/A';
    };

    // Get rights requests summary for a DP
    const getRightsRequestsSummary = (dpId: number): DPRightsRequestsSummary | null => {
        return rightsRequestsCache.get(dpId) || null;
    };

    if (loading && dataPrincipals.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && dataPrincipals.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Error Loading Data Principals
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {error}
                    </Typography>
                    <Button variant="contained" onClick={() => {
                        loadDataPrincipals();
                        loadStats();
                    }} sx={{ mt: 2 }}>
                        Retry
                    </Button>
                </Box>
            </Container>
        );
    }

    const allSelected = dataPrincipals.length > 0 && selectedDPs.size === dataPrincipals.length;
    const someSelected = selectedDPs.size > 0 && selectedDPs.size < dataPrincipals.length;

    return (
        <Container maxWidth="xl">
            <Global
                styles={{
                    body: {
                        backgroundColor: theme.palette.background.default,
                    },
                }}
            />
            <Box sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'text.primary' }}>
                                Data Principals
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Manage and view data principal information, consent status, and rights requests
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {selectedDPs.size > 0 && (
                            <Button
                                variant="contained"
                                startIcon={<AssignmentIcon />}
                                onClick={() => setIsBulkAssignModalOpen(true)}
                            >
                                Bulk assign notice ({selectedDPs.size})
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setIsAddPanelOpen(true)}
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark
                                }
                            }}
                        >
                            ADD NEW
                        </Button>
                    </Box>
                </Box>

                {/* Statistics Cards */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        Overview
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            pb: 2,
                            px: 1,
                            '&::-webkit-scrollbar': {
                                height: 8,
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                borderRadius: 4,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                                borderRadius: 4,
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)',
                                },
                            },
                        }}
                    >
                        {statsLoading ? (
                            Array.from({ length: 4 }).map((_, index) => (
                                <Skeleton
                                    key={index}
                                    variant="rectangular"
                                    width={160}
                                    height={100}
                                    sx={{ borderRadius: 2, flexShrink: 0 }}
                                />
                            ))
                        ) : (
                            <>
                                <Card sx={statCardSx('total')}>
                                    <CardActionArea onClick={() => handleStatCardClick('total')}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {stats?.total ?? 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total DPs
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>

                                <Card sx={statCardSx('activeConsent')}>
                                    <CardActionArea onClick={() => handleStatCardClick('activeConsent')}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'success.main' }}>
                                                {stats?.activeConsent ?? 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Active Consent
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>

                                <Card sx={statCardSx('withdrawnConsent')}>
                                    <CardActionArea onClick={() => handleStatCardClick('withdrawnConsent')}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'error.main' }}>
                                                {stats?.withdrawnConsent ?? 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Withdrawn Consent
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>

                                <Card sx={statCardSx('test')}>
                                    <CardActionArea onClick={() => handleStatCardClick('test')}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {stats?.test ?? 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Test Data
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Filters and Search */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {searchError && (
                        <Box sx={{ width: '100%', mb: -1 }}>
                            <Typography variant="caption" color="error">
                                {searchError}
                            </Typography>
                        </Box>
                    )}
                    {searchType === "PHONE" ? (
                        <>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel id="search-type">Search By</InputLabel>
                                <Select
                                    labelId="search-type"
                                    label="Search By"
                                    value={searchType}
                                    onChange={(e) => {
                                        setSearchType(e.target.value as SearchType);
                                        setSearchInput("");
                                        setPhoneNumber("");
                                        setSearchError(null);
                                    }}
                                >
                                    <MenuItem value="DPID">DPId</MenuItem>
                                    <MenuItem value="TOKEN">Token</MenuItem>
                                    <MenuItem value="EXTERNAL_ID">External ID</MenuItem>
                                    <MenuItem value="EMAIL">Email</MenuItem>
                                    <MenuItem value="PHONE">Phone</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel id="phone-country">Country</InputLabel>
                                <Select
                                    labelId="phone-country"
                                    label="Country"
                                    value={phoneCountryCode}
                                    onChange={(e) => {
                                        setPhoneCountryCode(e.target.value);
                                        setSearchError(null);
                                    }}
                                >
                                    {COUNTRY_CODES.map((cc) => (
                                        <MenuItem key={cc.code} value={cc.dialCode}>
                                            {cc.dialCode} ({cc.name})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                placeholder={phoneCountryCode === "+91" ? "9876543210" : "Enter phone number"}
                                variant="outlined"
                                size="small"
                                value={phoneNumber}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    setPhoneNumber(value);
                                    setSearchError(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                    endAdornment: phoneNumber && (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setPhoneNumber('');
                                                const newFilters = { ...filters, search: undefined, page: 1 };
                                                setFilters(newFilters);
                                                setPage(0);
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    ),
                                }}
                                inputProps={{ 
                                    maxLength: phoneCountryCode === "+91" || phoneCountryCode === "+1" ? 10 : 15,
                                    inputMode: "numeric"
                                }}
                                error={!!searchError}
                                sx={{ flexGrow: 1, minWidth: 200 }}
                            />
                        </>
                    ) : (
                        <>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel id="search-type">Search By</InputLabel>
                                <Select
                                    labelId="search-type"
                                    label="Search By"
                                    value={searchType}
                                    onChange={(e) => {
                                        setSearchType(e.target.value as SearchType);
                                        setSearchInput("");
                                        setPhoneNumber("");
                                        setSearchError(null);
                                    }}
                                >
                                    <MenuItem value="DPID">DPId</MenuItem>
                                    <MenuItem value="TOKEN">Token</MenuItem>
                                    <MenuItem value="EXTERNAL_ID">External ID</MenuItem>
                                    <MenuItem value="EMAIL">Email</MenuItem>
                                    <MenuItem value="PHONE">Phone</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                placeholder={
                                    searchType === "DPID" ? "Enter DPId..." :
                                    searchType === "TOKEN" ? "Enter token..." :
                                    searchType === "EXTERNAL_ID" ? "Enter external ID..." :
                                    "Enter email address..."
                                }
                                variant="outlined"
                                size="small"
                                value={searchInput}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    if (searchType === "DPID") {
                                        value = value.replace(/\D/g, "");
                                    }
                                    setSearchInput(value);
                                    setSearchError(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                    endAdornment: searchInput && (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchInput('');
                                                setPhoneNumber('');
                                                const newFilters = { ...filters, search: undefined, page: 1 };
                                                setFilters(newFilters);
                                                setPage(0);
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    ),
                                }}
                                type={searchType === "EMAIL" ? "email" : searchType === "DPID" ? "number" : "text"}
                                error={!!searchError}
                                sx={{ flexGrow: 1, minWidth: 200 }}
                            />
                        </>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Search
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setIsSendConsentModalOpen(true)}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Send Consent Notice
                    </Button>
                    <IconButton onClick={loadDataPrincipals} color="primary">
                        <RefreshIcon />
                    </IconButton>
                    <Button
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        onClick={handleFilterClick}
                        sx={{ borderColor: hasActiveFilters() ? 'primary.main' : 'divider' }}
                    >
                        Filters
                        {hasActiveFilters() && (
                            <Chip
                                label={Object.keys(filters).filter(key => filters[key as keyof DataPrincipalFilters] !== undefined && key !== 'page' && key !== 'pageSize').length}
                                size="small"
                                sx={{ ml: 1, height: 20 }}
                            />
                        )}
                    </Button>
                </Box>

                {/* Applied Filters */}
                {hasActiveFilters() && (
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {filters.identifierType && (
                            <Chip
                                label={`Type: ${filters.identifierType}`}
                                onDelete={() => removeFilter('identifierType')}
                                size="small"
                            />
                        )}
                        {filters.consentStatus && (
                            <Chip
                                label={`Status: ${filters.consentStatus}`}
                                onDelete={() => removeFilter('consentStatus')}
                                size="small"
                            />
                        )}
                        {filters.isTest !== undefined && (
                            <Chip
                                label={filters.isTest ? 'Test Data' : 'Production Data'}
                                onDelete={() => removeFilter('isTest')}
                                size="small"
                            />
                        )}
                        {filters.isActive !== undefined && (
                            <Chip
                                label={filters.isActive ? 'Active' : 'Inactive'}
                                onDelete={() => removeFilter('isActive')}
                                size="small"
                            />
                        )}
                        {filters.startDate && (
                            <Chip
                                label={`From: ${new Date(filters.startDate).toLocaleDateString()}`}
                                onDelete={() => removeFilter('startDate')}
                                size="small"
                            />
                        )}
                        {filters.endDate && (
                            <Chip
                                label={`To: ${new Date(filters.endDate).toLocaleDateString()}`}
                                onDelete={() => removeFilter('endDate')}
                                size="small"
                            />
                        )}
                        <Button size="small" onClick={handleClearFilters}>
                            Clear All
                        </Button>
                    </Box>
                )}

                {/* Phase 1 dummy modal to lock UX */}
                <SendConsentNoticeModal
                    open={isSendConsentModalOpen}
                    onClose={() => setIsSendConsentModalOpen(false)}
                />

                <BulkAssignNoticeModal
                    open={isBulkAssignModalOpen}
                    onClose={() => setIsBulkAssignModalOpen(false)}
                    dpIds={Array.from(selectedDPs)}
                    onSuccess={() => {
                        setSelectedDPs(new Set());
                        loadDataPrincipals();
                    }}
                />

                {/* Filter Popover */}
                <Popover
                    open={Boolean(filterAnchorEl)}
                    anchorEl={filterAnchorEl}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 3, minWidth: 300 }}>
                        <Typography variant="h6" gutterBottom>
                            Filters
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Identifier Type</InputLabel>
                                <Select
                                    value={tempFilters.identifierType || ''}
                                    label="Identifier Type"
                                    onChange={(e) => handleTempFilterChange('identifierType', e.target.value || undefined)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="EMAIL">Email</MenuItem>
                                    <MenuItem value="PHONE">Phone</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Consent Status</InputLabel>
                                <Select
                                    value={tempFilters.consentStatus || ''}
                                    label="Consent Status"
                                    onChange={(e) => handleTempFilterChange('consentStatus', e.target.value || undefined)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                                    <MenuItem value="WITHDRAWN">Withdrawn</MenuItem>
                                    <MenuItem value="EXPIRED">Expired</MenuItem>
                                    <MenuItem value="UNKNOWN">Unknown</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Data Type</InputLabel>
                                <Select
                                    value={tempFilters.isTest === undefined ? '' : tempFilters.isTest ? 'test' : 'production'}
                                    label="Data Type"
                                    onChange={(e) => handleTempFilterChange('isTest', e.target.value === '' ? undefined : e.target.value === 'test')}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="test">Test Data</MenuItem>
                                    <MenuItem value="production">Production Data</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Active Status</InputLabel>
                                <Select
                                    value={tempFilters.isActive === undefined ? '' : tempFilters.isActive ? 'active' : 'inactive'}
                                    label="Active Status"
                                    onChange={(e) => handleTempFilterChange('isActive', e.target.value === '' ? undefined : e.target.value === 'active')}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label="Start Date"
                                type="date"
                                size="small"
                                fullWidth
                                value={tempFilters.startDate ? tempFilters.startDate.split('T')[0] : ''}
                                onChange={(e) => handleTempFilterChange('startDate', e.target.value ? `${e.target.value}T00:00:00Z` : undefined)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                label="End Date"
                                type="date"
                                size="small"
                                fullWidth
                                value={tempFilters.endDate ? tempFilters.endDate.split('T')[0] : ''}
                                onChange={(e) => handleTempFilterChange('endDate', e.target.value ? `${e.target.value}T23:59:59Z` : undefined)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                            <Button onClick={handleFilterClose}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                disabled={filterLoading}
                            >
                                Apply
                            </Button>
                        </Box>
                    </Box>
                </Popover>

                {/* Table */}
                <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={someSelected}
                                        checked={allSelected}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                                <TableCell>DP ID</TableCell>
                                <TableCell>Identifier</TableCell>
                                <TableCell>External ID</TableCell>
                                <TableCell>Consent Status</TableCell>
                                <TableCell>Last Consent</TableCell>
                                <TableCell>Last Activity</TableCell>
                                <TableCell>Rights Requests</TableCell>
                                <TableCell>Created</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: pageSize }).map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell colSpan={9}>
                                            <Skeleton variant="rectangular" height={53} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : dataPrincipals.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No data principals found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                dataPrincipals.map((dp) => {
                                    const rightsSummary = getRightsRequestsSummary(dp.dpId);
                                    const statusColors = getConsentStatusColor(dp.overallConsentStatus, theme.palette.mode === 'dark');
                                    return (
                                        <TableRow
                                            key={dp.dpId}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleDPClick(dp.dpId)}
                                        >
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedDPs.has(dp.dpId)}
                                                    onChange={() => handleSelectDP(dp.dpId)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="body2">{dp.dpId}</Typography>
                                                    {dp.isMinor && (
                                                        <Chip
                                                            label="Minor"
                                                            size="small"
                                                            color="warning"
                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {dp.identifierType === 'EMAIL' ? (
                                                        <EmailIcon fontSize="small" color="action" />
                                                    ) : (
                                                        <PhoneIcon fontSize="small" color="action" />
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDPClick(dp.dpId);
                                                        }}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                    >
                                                        {formatIdentifier(dp)}
                                                    </Typography>
                                                    {dp.isTest && (
                                                        <Tooltip
                                                            title={
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                                        Test Data
                                                                    </Typography>
                                                                    <Typography variant="caption">
                                                                        This data principal record contains test data. Identifier values are stored directly in the database (not in vault) and are visible for testing purposes.
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            arrow
                                                            placement="top"
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        maxWidth: 300,
                                                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                                                        '& .MuiTooltip-arrow': {
                                                                            color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <Chip label="Test" size="small" color="warning" sx={{ cursor: 'help' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {dp.externalIdentifier ? (
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {dp.externalIdentifier}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip
                                                    title={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                                {dp.overallConsentStatus}
                                                            </Typography>
                                                            <Typography variant="caption">
                                                                {getConsentStatusDescription(dp.overallConsentStatus)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    arrow
                                                    placement="top"
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                maxWidth: 300,
                                                                bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                                                '& .MuiTooltip-arrow': {
                                                                    color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Chip
                                                        label={dp.overallConsentStatus}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: statusColors.bg,
                                                            color: statusColors.color,
                                                            border: `1px solid ${statusColors.border}`,
                                                            cursor: 'help',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {dp.lastConsentDate ? (
                                                    <Tooltip title={new Date(dp.lastConsentDate).toLocaleString()}>
                                                        <Typography variant="body2">
                                                            {formatFriendlyDate(dp.lastConsentDate)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {dp.lastActivityDate ? (
                                                    <Tooltip title={new Date(dp.lastActivityDate).toLocaleString()}>
                                                        <Typography variant="body2">
                                                            {formatFriendlyDate(dp.lastActivityDate)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                {rightsSummary && rightsSummary.totalOpen > 0 ? (
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {rightsSummary.dataAccess.count > 0 && (
                                                            <Tooltip title={`Data Access: ${rightsSummary.dataAccess.count}`}>
                                                                <Chip label={`🔍${rightsSummary.dataAccess.count}`} size="small" />
                                                            </Tooltip>
                                                        )}
                                                        {rightsSummary.dataCorrection.count > 0 && (
                                                            <Tooltip title={`Data Correction: ${rightsSummary.dataCorrection.count}`}>
                                                                <Chip label={`📝${rightsSummary.dataCorrection.count}`} size="small" />
                                                            </Tooltip>
                                                        )}
                                                        {rightsSummary.dataErasure.count > 0 && (
                                                            <Tooltip title={`Data Erasure: ${rightsSummary.dataErasure.count}`}>
                                                                <Chip label={`🗑️${rightsSummary.dataErasure.count}`} size="small" />
                                                            </Tooltip>
                                                        )}
                                                        {rightsSummary.dataPortability.count > 0 && (
                                                            <Tooltip title={`Data Portability: ${rightsSummary.dataPortability.count}`}>
                                                                <Chip label={`📦${rightsSummary.dataPortability.count}`} size="small" />
                                                            </Tooltip>
                                                        )}
                                                        {rightsSummary.consentWithdrawal.count > 0 && (
                                                            <Tooltip title={`Consent Withdrawal: ${rightsSummary.consentWithdrawal.count}`}>
                                                                <Chip label={`❌${rightsSummary.consentWithdrawal.count}`} size="small" />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {dp.createdDate ? (
                                                    <Tooltip title={new Date(dp.createdDate).toLocaleString()}>
                                                        <Typography variant="body2">
                                                            {formatFriendlyDate(dp.createdDate)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={handlePageSizeChange}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    sx={{ bgcolor: 'background.paper' }}
                />
            </Box>

            {/* Add Data Principal Panel */}
            <AddDataPrincipalPanel
                open={isAddPanelOpen}
                onClose={() => setIsAddPanelOpen(false)}
                onSave={() => {
                    loadDataPrincipals();
                    loadStats();
                }}
            />
        </Container>
    );
};

export default DataPrincipals;

